import React, {forwardRef, useCallback, useEffect, useState} from "react";
import {
    Box, FormControl, FormLabel, Typography
} from "@mui/material";
import {DateValidationError, DesktopDatePicker, PickerChangeHandlerContext} from "@mui/x-date-pickers";
import moment, {Moment} from "moment";
import {useController} from "react-hook-form";
import {FieldPath, FieldValues} from "react-hook-form/dist/types";

type DateInputProps<T extends FieldValues, TName extends FieldPath<T>> = {
    name: TName;
    control?: any;
    hint?: string;
    error?: boolean;
    label?: string;
    errorMessage?: string;
    fieldMapper?: (value: Moment | null) => string | Date | undefined;
    clearable?: boolean;
    ignoreTimeZone?: boolean;
    readonly?: boolean;
    disabled?: boolean;
}

const DateInput = forwardRef(<T extends FieldValues, TName extends FieldPath<T>>(
    props: DateInputProps<T, TName>, 
    ref: React.Ref<HTMLInputElement>
) => {

    const [innerValue, setInnerValue] = useState<Moment | null>(null);

    const {
        error,
        errorMessage, 
        hint, 
        label, 
        readonly, 
        disabled, 
        clearable,
        ignoreTimeZone,
        fieldMapper,
        ...rest
    } = props;

    const { field } = useController({
        defaultValue: undefined,
        ...rest
    });

    useEffect(() => {
        if (!field.value) {
            setInnerValue(null);
        } else {
            setInnerValue(moment(field.value));
        }
    }, [field.value])

    const change = useCallback((value: Moment | null, context: PickerChangeHandlerContext<DateValidationError>) => {
        let output;

        if (ignoreTimeZone) {
            // Treat as local date without time zone considerations
            output = value ? moment(value).startOf('day').format("YYYY-MM-DDTHH:mm:ss") : undefined;
        } else {
            // Treat with time zone awareness
            output = value?.toDate();
        }

        if (fieldMapper) {
            output = fieldMapper(value);
        }

        return field.onChange(output);

    }, [field, props])

    return (
        <Box>
            <FormControl error={error} fullWidth>
                {label && <FormLabel id={`${field.name}-label`} className={"input-label"} sx={{mb: 0.5}}>{label}</FormLabel>}

                <DesktopDatePicker
                    format="DD/MM/YYYY"
                    className={"input-field"}
                    onChange={change}
                    value={innerValue}
                    disabled={disabled}
                    readOnly={readonly}
                    maxDate={moment().subtract(13, 'years')}
                    disableFuture
                    slotProps={{
                        textField: {
                            id: field.name, 
                            error: error, 
                            fullWidth: true,
                            inputRef: ref,
                        },
                        field: {clearable: clearable, onClear: () => field.onChange(null)}
                    }}

                />
                {props.errorMessage &&
                    <Typography component="div" className={"error-message"} variant={"body2"}>
                        {errorMessage}
                    </Typography>
                }
                {props.hint &&
                    <Typography component="div" className={"input-help"} variant={"body2"}>
                        {hint}
                    </Typography>
                }
            </FormControl>

        </Box>
    );
});

export {DateInput}
